import { useEffect, useRef } from 'react';
import { showTooltip, hideTooltip } from './Tooltip';
import { formatNumber, formatPercent, formatWithUnit } from '../../lib/fmt';

export type DonutDatum = { key: string; label: string; value: number; color?: string };

const PALETTE = [
  'var(--chart-c1)',
  'var(--chart-c3)',
  'var(--chart-c2)',
  'var(--chart-c4)',
  'var(--chart-c5)',
  'var(--chart-c7)',
  'var(--chart-c6)',
];

export default function Donut({
  data,
  size = 220,
  thickness = 44,
  onClickSlice,
  activeKey,
  centerLabel,
  centerValue,
  unit,
  legend = 'right',
}: {
  data: DonutDatum[];
  size?: number;
  thickness?: number;
  onClickSlice?: (d: DonutDatum) => void;
  activeKey?: string | null;
  centerLabel?: string;
  centerValue?: string | number;
  unit?: string | null;
  legend?: 'right' | 'bottom' | 'none';
}) {
  const safeDonut: DonutDatum[] = (Array.isArray(data) ? data : [])
    .filter((d): d is DonutDatum => !!d)
    .map((d, i) => ({
      key: String(d.key ?? d.label ?? `k${i}`),
      label: String(d.label ?? d.key ?? ''),
      value: Number(d.value ?? 0),
      color: d.color ?? PALETTE[i % PALETTE.length],
    }));

  const total = safeDonut.reduce((a, b) => a + (b.value || 0), 0) || 0;
  const r = size / 2;
  const ir = Math.max(8, r - thickness);
  const gRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (!gRef.current) return;
    const paths = gRef.current.querySelectorAll<SVGPathElement>('path[data-arc]');
    paths.forEach((p, i) => {
      const len = p.getTotalLength?.() ?? 400;
      p.style.transition = 'none';
      p.style.strokeDasharray = `${len}`;
      p.style.strokeDashoffset = `${len}`;
      p.style.stroke = 'transparent';
      requestAnimationFrame(() => {
        p.style.transition = `stroke-dashoffset 700ms ease ${i * 60}ms, transform 200ms`;
        p.style.strokeDashoffset = '0';
      });
    });
  }, [safeDonut.map((d) => d.key + d.value).join('|')]);

  // Texte central : si fourni et trop long, on le rejette sous le donut
  const rawCenter =
    centerValue != null
      ? String(centerValue)
      : unit
      ? formatWithUnit(total, unit)
      : formatNumber(total);
  const centerIsLong = rawCenter.length > 10;
  const showCenterBelow = centerIsLong;

  // Précalcule les arcs (on n'affiche plus les labels % sur les segments quand il y a une légende)
  let ang0 = -Math.PI / 2;
  const arcs = safeDonut.map((d, i) => {
    const frac = total > 0 ? d.value / total : 0;
    const ang1 = ang0 + frac * Math.PI * 2;
    const large = frac > 0.5 ? 1 : 0;
    const p0 = [Math.cos(ang0) * r, Math.sin(ang0) * r];
    const p1 = [Math.cos(ang1) * r, Math.sin(ang1) * r];
    const p2 = [Math.cos(ang1) * ir, Math.sin(ang1) * ir];
    const p3 = [Math.cos(ang0) * ir, Math.sin(ang0) * ir];
    const path =
      frac === 0
        ? ''
        : frac >= 0.999
        ? `M ${r - 0.01} 0 A ${r} ${r} 0 1 1 ${r} 0 L ${ir} 0 A ${ir} ${ir} 0 1 0 ${ir - 0.01} 0 Z`
        : `M ${p0[0]} ${p0[1]} A ${r} ${r} 0 ${large} 1 ${p1[0]} ${p1[1]} L ${p2[0]} ${p2[1]} A ${ir} ${ir} 0 ${large} 0 ${p3[0]} ${p3[1]} Z`;
    const data = { ...d, frac, index: i, path };
    ang0 = ang1;
    return data;
  });

  const donutSvg = (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible', flexShrink: 0 }}>
      <g ref={gRef} transform={`translate(${r} ${r})`}>
        {arcs.map((a) => {
          const isActive = activeKey === a.key;
          const dim = activeKey && !isActive;
          return (
            <path
              key={a.key}
              d={a.path}
              data-arc
              stroke="#fff"
              strokeWidth="1.5"
              style={{
                fill: a.color,
                cursor: onClickSlice ? 'pointer' : 'default',
                opacity: dim ? 0.35 : 1,
                transform: isActive ? 'scale(1.04)' : 'scale(1)',
                transformOrigin: 'center',
                transition: 'opacity 180ms, transform 180ms',
              }}
              onMouseMove={(e) =>
                showTooltip({
                  x: e.clientX,
                  y: e.clientY,
                  title: a.label,
                  rows: [
                    { label: 'Valeur', value: formatWithUnit(a.value, unit) },
                    { label: 'Part', value: formatPercent(a.frac) },
                  ],
                })
              }
              onMouseLeave={hideTooltip}
              onClick={() => onClickSlice?.(a as DonutDatum)}
            />
          );
        })}

        {/* Texte au centre seulement quand il tient (courte valeur) */}
        {!showCenterBelow && (
          <>
            <text
              x={0}
              y={-4}
              textAnchor="middle"
              fontSize="10"
              style={{ fill: 'var(--chart-text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}
            >
              {centerLabel ?? 'Total'}
            </text>
            <text
              x={0}
              y={18}
              textAnchor="middle"
              fontSize={size < 180 ? 18 : 22}
              fontWeight="700"
              style={{ fill: 'var(--chart-text)' }}
            >
              {rawCenter}
            </text>
          </>
        )}
      </g>
    </svg>
  );

  if (legend === 'none') {
    return showCenterBelow ? (
      <div className="donut-wrap donut-legend-bottom">
        {donutSvg}
        <div className="donut-center-below">
          <div className="donut-cb-label">{centerLabel ?? 'Total'}</div>
          <div className="donut-cb-value">{rawCenter}</div>
        </div>
      </div>
    ) : (
      donutSvg
    );
  }

  const legendItems = (
    <ul className="donut-legend">
      {safeDonut.map((d, i) => {
        const frac = total > 0 ? d.value / total : 0;
        const isActive = activeKey === d.key;
        const dim = activeKey && !isActive;
        return (
          <li
            key={d.key}
            className={`donut-legend-item${dim ? ' dim' : ''}${isActive ? ' active' : ''}`}
            onClick={() => onClickSlice?.(d)}
            onMouseEnter={(e) =>
              showTooltip({
                x: e.clientX,
                y: e.clientY,
                title: d.label,
                rows: [
                  { label: 'Valeur', value: formatWithUnit(d.value, unit) },
                  { label: 'Part', value: formatPercent(frac) },
                ],
              })
            }
            onMouseLeave={hideTooltip}
            style={{ cursor: onClickSlice ? 'pointer' : 'default' }}
          >
            <span className="donut-legend-dot" style={{ background: d.color ?? PALETTE[i % PALETTE.length] }} />
            <span className="donut-legend-label" title={d.label}>{d.label}</span>
            <span className="donut-legend-pct">{formatPercent(frac)}</span>
            <span className="donut-legend-value">{formatWithUnit(d.value, unit)}</span>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={`donut-wrap donut-legend-${legend}`}>
      {donutSvg}
      {showCenterBelow && (
        <div className="donut-center-below">
          <div className="donut-cb-label">{centerLabel ?? 'Total'}</div>
          <div className="donut-cb-value">{rawCenter}</div>
        </div>
      )}
      {legendItems}
    </div>
  );
}
